import React, { useState } from 'react';
import HistoryRange from 'types/Meteorology/Weather/Area/historyRange';
import downloadFile from 'helper/downloadFile';
import range from 'helper/range';
import { filterHistoryData } from 'helper/Meteorology/Weather/Area/History';
import { Card, Col } from 'react-bootstrap';
import Button from 'components/DesignSystem/Button';
import useIsMobile from 'utils/hooks/useIsMobile';
import FullscreenChart from 'components/Layout/MobileShell/FullscreenChart';
import { cssVar } from 'constants/theme';
import DataRangeDisplay from '../DataRangeDisplay';
import HistoryGraph from '../HistoryGraph';
import RangeSelector from '../RangeSelector';

import '../Mobile/index.scss';
import './index.scss';

interface HistoryCardProps {
  areaAcronym: string,
  areaLabel: string,
  valueData: number[],
  categoryData: string[],
  graphPositiveColor?: string,
}

export default function HistoryCard({
  areaAcronym,
  areaLabel,
  valueData,
  categoryData,
  graphPositiveColor,
}: HistoryCardProps) {
  const isMobile = useIsMobile();
  const [selectedRange, setSelectedRange] = useState<HistoryRange>('all');
  const [fullscreen, setFullscreen] = useState(false);
  const { filteredCategories, filteredValues } = filterHistoryData(
    new Date(),
    selectedRange,
    valueData,
    categoryData,
  );
  const rowsAvailable = Math.min(filteredCategories.length, filteredValues.length);
  const downloadCsv = () => {
    const rows = range(rowsAvailable).map((i) => `${filteredCategories[i]},${filteredValues[i]}`);
    const csvData = btoa(`Date,Value\n${rows.join('\n')}`);
    downloadFile(csvData, `historico_${selectedRange}.csv`);
  };

  const downloadButton = (
    <Button
      id="weather-history-download"
      small
      disabled={rowsAvailable < 1}
      icon="download"
      onClick={downloadCsv}
      className={isMobile ? '' : 'ms-3'}
    />
  );

  const graph = (
    <HistoryGraph
      categoryData={filteredCategories}
      valueData={filteredValues}
      positiveColor={graphPositiveColor}
    />
  );

  if (isMobile) {
    const mobileTitle = `${areaLabel} (${areaAcronym.toUpperCase()}) · Mensal`;
    return (
      <div className="history-card-mobile">
        <div className="hcm-head">
          <span className="hcm-title">{mobileTitle}</span>
          <div className="weather-history-actions">
            <button
              type="button"
              aria-label="Tela cheia"
              className="weather-history-fullscreen-trigger"
              onClick={() => setFullscreen(true)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={cssVar('text-strong')} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                <path d="M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M8 21H5a2 2 0 0 1-2-2v-3M16 21h3a2 2 0 0 0 2-2v-3" />
              </svg>
            </button>
            {downloadButton}
          </div>
        </div>
        <div className="hcm-range">
          <RangeSelector
            selection={selectedRange}
            setSelection={setSelectedRange}
          />
        </div>
        <DataRangeDisplay
          firstDate={categoryData.at(0) || ''}
          lastDate={categoryData.at(-1) || ''}
        />
        {graph}
        <FullscreenChart
          open={fullscreen}
          dismiss={() => setFullscreen(false)}
          title={mobileTitle}
        >
          <HistoryGraph
            categoryData={filteredCategories}
            valueData={filteredValues}
            positiveColor={graphPositiveColor}
            fill
          />
        </FullscreenChart>
      </div>
    );
  }

  return (
    <Card className="shadow-sm forecast-instruction-card m-5">
      <Col className="flex-fill py-5 h-100">
        <div className="d-flex px-5 justify-content-between align-items-center">
          <div className="text-primary">{areaLabel}</div>
          <div className="text-primary fw-bold d-flex align-items-center">
            Baixar dados em CSV
            {downloadButton}
          </div>
        </div>
        <div className="mt-5">
          <div className="d-flex justify-content-between align-items-center px-5">
            <RangeSelector
              selection={selectedRange}
              setSelection={setSelectedRange}
            />
            <p className="text-primary">{`${areaLabel} (${areaAcronym.toUpperCase()}) - Mensal`}</p>
            <DataRangeDisplay
              firstDate={categoryData.at(0) || ''}
              lastDate={categoryData.at(-1) || ''}
            />
          </div>
          {graph}
        </div>
      </Col>
    </Card>
  );
}
